import * as React from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CLAIMS_CHANGED_MESSAGE, CLAIMS_MODAL_CLOSE_MESSAGE } from "../lib/demoMode";

/**
 * In-page modal that shows one of the /popup/* claim views in an iframe, so
 * it opens over the Overview in the same window. The iframe is same-origin
 * (the signed-in session carries over) and reuses the existing popup routes,
 * including the list -> claim detail navigation.
 */
export function ClaimsModal({ path, onClose }: { path: string; onClose: () => void }) {
  const queryClient = useQueryClient();

  // Everything on the Overview that depends on claim status.
  const refreshOverview = React.useCallback(() => {
    for (const key of ["dashboard", "company-agreements", "claim-candidates", "activity", "cil"]) {
      queryClient.invalidateQueries({ queryKey: [key] });
    }
  }, [queryClient]);

  const close = React.useCallback(() => {
    refreshOverview();
    onClose();
  }, [refreshOverview, onClose]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Escape inside HP Genie (which sits above this popup) is Genie's own.
      if (e.target instanceof Element && e.target.closest("[data-genie]")) return;
      if (e.key === "Escape") close();
    };
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data === CLAIMS_MODAL_CLOSE_MESSAGE) close();
      else if (e.data === CLAIMS_CHANGED_MESSAGE) refreshOverview();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("message", onMessage);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("message", onMessage);
      document.body.style.overflow = previousOverflow;
    };
  }, [close, refreshOverview]);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-zinc-900/50 p-3 sm:p-6"
      onClick={close}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Claims"
        className="h-full max-h-[56rem] w-full max-w-[77.5rem] overflow-hidden rounded-xl bg-white shadow-2xl 2xl:max-h-[64rem] 2xl:max-w-[96rem]"
        onClick={(e) => e.stopPropagation()}
      >
        <iframe title="Claims" data-popup-frame src={path} className="h-full w-full border-0" />
      </div>
    </div>,
    document.body,
  );
}
